/**
 * Page locking commands backed by the new pagination system.
 * These are the counterparts of the commands in locked-pages.ts, which
 * use the legacy PaginationBreakdown. Use these when the new pagination
 * plugin is active.
 */

import { type Command, type Transaction } from 'prosemirror-state'
import { type EditorView } from 'prosemirror-view'

import { constants } from '@showrunner/prose-schemas'

import { cleanLockedPageBreak, withLockedPageBreak } from './pageBreakMarkers'
import { type LockedPageEntry } from './PageLockMap'
import { getLockedPageInventory, getPaginationPages } from './paginationPlugin'
import { BasePage } from './Paginator'

const { NodeTypeMap } = constants

type PageLockTarget = {
  start: number
  end: number
  pageNumber: string | number
}
const focus = (view: EditorView | undefined) => {
  if (!view) return
  requestAnimationFrame(() => {
    view.focus()
  })
}

export const lockAllPages: Command = (state, dispatch, view) => {
  const inventory = getLockedPageInventory(state)
  const pages = getPaginationPages(state)
  if (!inventory || !pages) return false

  const pageNumbers = inventory.entries.map((e) => e.pageNumber)
  const pagesToLock = getPagesToLock(pageNumbers, inventory.entries, pages)
  if (pagesToLock.length === 0) return false

  const { tr } = state
  const pageLock = (target: PageLockTarget) => lockPage(tr, target)
  pagesToLock.reverse().forEach(pageLock)

  if (dispatch) {
    dispatch(tr)
    focus(view)
  }
  return tr.steps.length > 0
}

export function lockPages(pageNumbers: number[]): Command {
  return (state, dispatch, view) => {
    const inventory = getLockedPageInventory(state)
    const pages = getPaginationPages(state)
    if (!inventory || !pages) return false

    const pagesToLock = getPagesToLock(pageNumbers, inventory.entries, pages)
    if (pagesToLock.length === 0) return false

    const { tr } = state
    const pageLock = (target: PageLockTarget) => lockPage(tr, target)
    pagesToLock.reverse().forEach(pageLock)
    if (dispatch) {
      dispatch(tr)
      focus(view)
    }

    return tr.steps.length > 0
  }
}

function getPagesToLock(
  pageNumbers: number[],
  entries: LockedPageEntry[],
  pages: BasePage[],
): PageLockTarget[] {
  const targets: PageLockTarget[] = []

  for (const [pageIndex, entry] of entries.entries()) {
    if (!pageNumbers.includes(entry.pageNumber) || entry.isLocked) continue

    const page = pages[pageIndex]
    if (!page) continue
    targets.push({
      start: page.start,
      end: page.end,
      pageNumber: entry.alias ?? entry.pageNumber,
    })
  }

  return targets
}

function lockPage(tr: Transaction, target: PageLockTarget): void {
  const { start, end } = target
  splitPage(tr, end)
  splitPage(tr, start)
  setLockedPageBreakAttr(tr, tr.mapping.map(start), target.pageNumber)
}

function setLockedPageBreakAttr(
  tr: Transaction,
  pos: number,
  pageNumber: string | number,
): void {
  const blockStartPos = resolveBlockStartPos(tr, pos)
  const existing = tr.doc.nodeAt(blockStartPos)?.attrs.pageBreak
  const wasManual = existing === 'manual'
  tr.setNodeAttribute(
    blockStartPos,
    'pageBreak',
    withLockedPageBreak(pageNumber, wasManual),
  )
}

function resolveBlockStartPos(tr: Transaction, pos: number): number {
  const $pos = tr.doc.resolve(pos)
  if ($pos.nodeAfter?.isBlock) return pos

  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth)
    if (node.isBlock) {
      return $pos.before(depth)
    }
  }

  return pos
}

function splitPage(tr: Transaction, pos: number): void {
  const $pos = tr.doc.resolve(pos)
  if ($pos.parent.type.name !== NodeTypeMap.DIALOGUE) return
  if ($pos.parentOffset <= 0 || $pos.parentOffset >= $pos.parent.content.size) {
    return
  }

  tr.split(pos, 1, [
    {
      type: $pos.parent.type,
      attrs: {
        ...removeAttrsId($pos.parent.attrs),
        pageBreak: null,
      },
    },
  ])
}

export const unlockAllPages: Command = (state, dispatch, view) => {
  const inventory = getLockedPageInventory(state)

  const { tr } = state
  clearAllPageBreakAttrs(tr)
  inventory?.entries
    .slice(1)
    .map((entry) => entry.startPos)
    .reverse()
    .forEach((startPos) => mergeDialoguesAtPageBoundary(tr, startPos))
  if (tr.steps.length > 0 && dispatch) {
    dispatch(tr)
    focus(view)
  }
  return tr.steps.length > 0
}

export function unlockPages(pageNumbers: number[]): Command {
  return (state, dispatch, view) => {
    const inventory = getLockedPageInventory(state)
    if (!inventory) return false

    const entriesToUnlock = inventory.entries.filter(
      (e) => pageNumbers.includes(e.pageNumber) && e.isLocked,
    )

    if (entriesToUnlock.length === 0) return false

    const positionsToUnlock = entriesToUnlock.map((e) => e.startPos)

    const { tr } = state
    if (positionsToUnlock.length === inventory.lockedPages.length) {
      clearAllPageBreakAttrs(tr)
    } else {
      positionsToUnlock.forEach((pos) => {
        tr.setNodeAttribute(
          pos,
          'pageBreak',
          cleanLockedPageBreak(tr.doc.nodeAt(pos)?.attrs.pageBreak),
        )
      })
    }

    const unlockingPageNumbers = new Set(
      entriesToUnlock.map((entry) => entry.pageNumber),
    )
    const pageBoundaryStarts = pageStartsForUnlock(
      inventory.entries,
      unlockingPageNumbers,
    )
    pageBoundaryStarts.reverse().forEach((startPos) => {
      mergeDialoguesAtPageBoundary(tr, startPos)
    })

    if (dispatch) {
      dispatch(tr)
      focus(view)
    }

    return tr.steps.length > 0
  }
}

function clearAllPageBreakAttrs(tr: Transaction): void {
  tr.doc.descendants((node, pos, parent) => {
    if (parent?.type.name === NodeTypeMap.PAGE) {
      if (cleanLockedPageBreak(node.attrs.pageBreak) !== node.attrs.pageBreak) {
        tr.setNodeAttribute(
          pos,
          'pageBreak',
          cleanLockedPageBreak(node.attrs.pageBreak),
        )
      }
      return false
    }
  })
}

function mergeDialoguesAtPageBoundary(
  tr: Transaction,
  nextPageStartPos: number,
): boolean {
  const mappedNextPageStartPos = tr.mapping.map(nextPageStartPos)
  const nextNode = tr.doc.nodeAt(mappedNextPageStartPos)
  if (!nextNode || nextNode.type.name !== NodeTypeMap.DIALOGUE) {
    return false
  }

  const $beforeNext = tr.doc.resolve(mappedNextPageStartPos)
  const pageNode = $beforeNext.parent
  const nextIndex = $beforeNext.index()
  if (nextIndex < 1) return false

  const previousNode = pageNode.maybeChild(nextIndex - 1)
  if (!previousNode || previousNode.type.name !== NodeTypeMap.DIALOGUE) {
    return false
  }
  if (!previousNode.attrs.id) return false
  if (nextNode.attrs.pageBreak === 'manual') return false

  const pagePos = $beforeNext.before()
  let previousPos = pagePos + 1
  for (let i = 0; i < nextIndex - 1; i++) {
    previousPos += pageNode.child(i).nodeSize
  }

  const mergedNode = previousNode.type.create(
    previousNode.attrs,
    previousNode.content.append(nextNode.content),
    previousNode.marks,
  )

  tr.replaceWith(
    previousPos,
    mappedNextPageStartPos + nextNode.nodeSize,
    mergedNode,
  )
  return true
}

function pageStartsForUnlock(
  entries: LockedPageEntry[],
  unlockingPageNumbers: Set<number>,
): number[] {
  const isUnlockedAfterCommand = (pageNumber: number): boolean => {
    const entry = entries[pageNumber - 1]
    return Boolean(
      entry && (!entry.isLocked || unlockingPageNumbers.has(pageNumber)),
    )
  }

  return entries
    .filter(
      (entry) =>
        entry.pageNumber > 1 &&
        isUnlockedAfterCommand(entry.pageNumber - 1) &&
        isUnlockedAfterCommand(entry.pageNumber),
    )
    .map((entry) => entry.startPos)
}

export function deleteEmptyPage(pageNumber: number): Command {
  return (state, dispatch, view) => {
    const inventory = getLockedPageInventory(state)
    if (!inventory) return false

    const entry = inventory.getEntry(pageNumber)
    if (!entry?.isEmpty) return false

    const { tr } = state
    const $pos = tr.doc.resolve(entry.startPos + 1)
    const { parent } = $pos

    if (parent.content.size > 0) return false

    tr.deleteRange($pos.before(), $pos.after())
    if (dispatch) {
      dispatch(tr)
      focus(view)
    }

    return true
  }
}

export function setPageName(pageNumber: number, name: string): Command {
  return (state, dispatch, view) => {
    const inventory = getLockedPageInventory(state)
    if (!inventory) return false

    const entry = inventory.getEntry(pageNumber)
    if (!entry?.isLocked) return false

    if (dispatch) {
      const { tr } = state
      tr.setNodeAttribute(
        entry.startPos,
        'pageBreak',
        withLockedPageBreak(name),
      )
      dispatch(tr)
      focus(view)
    }

    return true
  }
}

export function clearPageName(pageNumber: number): Command {
  return (state, dispatch, view) => {
    const inventory = getLockedPageInventory(state)
    if (!inventory) return false

    const entry = inventory.getEntry(pageNumber)
    if (!entry?.isLocked) return false

    if (dispatch) {
      const { tr } = state
      tr.setNodeAttribute(
        entry.startPos,
        'pageBreak',
        withLockedPageBreak(pageNumber),
      )
      dispatch(tr)
      focus(view)
    }

    return true
  }
}

function removeAttrsId(
  attrs: Record<string, unknown>,
): Record<string, unknown> {
  const nextAttrs = { ...attrs }
  delete nextAttrs.id
  return nextAttrs
}
